import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Tag, Plus, Trash2, Edit2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Modal } from "../../components/shared/Modal";

export default function ManageMemberTypes() {
  const [types, setTypes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState("");
  const [fee, setFee] = useState("");
  const { authAxios } = useAuth();

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    try { const res = await authAxios.get("/members/types"); setTypes(res.data); }
    catch { toast.error("Failed to load member types"); }
  };

  const handleAdd = async () => {
    if (!name) return toast.error("Enter type name");
    try {
      await authAxios.post("/members/types", { name, annual_fee: parseFloat(fee) || 0 });
      toast.success("Member type created");
      setShowAdd(false); setName(""); setFee("");
      fetchTypes();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleUpdate = async () => {
    if (!name) return toast.error("Enter type name");
    try {
      await authAxios.put(`/members/types/${editItem.id}`, { name, annual_fee: parseFloat(fee) || 0 });
      toast.success("Updated");
      setEditItem(null); setName(""); setFee("");
      fetchTypes();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this member type?")) return;
    try { await authAxios.delete(`/members/types/${id}`); toast.success("Deleted"); fetchTypes(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
  };

  return (
    <div data-testid="manage-member-types">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-[#1e3a5f]">Member Types</h2>
        <button onClick={() => { setShowAdd(true); setName(""); setFee(""); }} className="w-10 h-10 rounded-full bg-[#8b5cf6] text-white flex items-center justify-center" data-testid="add-type-btn"><Plus className="w-5 h-5" /></button>
      </div>

      {types.length === 0 ? (
        <div className="text-center py-20 text-slate-400"><Tag className="w-16 h-16 mx-auto mb-4 opacity-30" /><p>No member types</p></div>
      ) : (
        <div className="space-y-3">
          {types.map(t => (
            <div key={t.id} className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center"><Tag className="w-5 h-5 text-purple-500" /></div>
                <div><p className="font-bold text-slate-800">{t.name}</p><p className="text-slate-500 text-sm">{"\u20ac"}{(t.annual_fee || 0).toFixed(2)} / year</p></div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditItem(t); setName(t.name); setFee(t.annual_fee?.toString() || "0"); }} className="p-2 text-blue-500 hover:bg-blue-50 rounded-full"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(t.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-full"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <Modal title="Add Member Type" onClose={() => setShowAdd(false)}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Type Name" className="w-full p-3 rounded-xl border mb-3" data-testid="type-name" />
        <input type="number" value={fee} onChange={e => setFee(e.target.value)} placeholder="Annual Fee (EUR)" className="w-full p-3 rounded-xl border mb-4" data-testid="type-fee" min="0" step="0.01" />
        <button onClick={handleAdd} className="w-full py-3 rounded-full bg-[#8b5cf6] text-white font-bold" data-testid="save-type">Add Type</button>
      </Modal>}

      {editItem && <Modal title="Edit Member Type" onClose={() => { setEditItem(null); setName(""); setFee(""); }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Type Name" className="w-full p-3 rounded-xl border mb-3" />
        <input type="number" value={fee} onChange={e => setFee(e.target.value)} placeholder="Annual Fee (EUR)" className="w-full p-3 rounded-xl border mb-4" min="0" step="0.01" />
        <button onClick={handleUpdate} className="w-full py-3 rounded-full bg-[#8b5cf6] text-white font-bold">Update</button>
      </Modal>}
    </div>
  );
}
